import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { IconAttach, IconChevronDown } from '@americanexpress/dls-icons';
import { formatBytes, formatFileName } from '../utils/utils';
import styles from './styles.css';

const badgeClassMap = {
  processing: styles.badgeProcessing,
  completed: styles.badgeCompleted,
  failed: styles.badgeFailed,
};

const DocList = ({
  retrievedDocs, filePreview, downloadProgress, locale,
}) => {
  const [expanded, setExpanded] = useState({});

  if (!retrievedDocs) {
    return <div />;
  }

  let allDocs = retrievedDocs;
  if (filePreview) {
    allDocs = retrievedDocs.map((doc) => {
      const dProg = downloadProgress.find((d) => d.fileId === doc.fileId);
      /* istanbul ignore next */
      if (dProg && doc.fileId === dProg.fileId) {
        return { ...doc, ...dProg };
      }
      return { ...doc, downloadProg: null };
    });
  }

  const docMap = allDocs.map((doc, idx) => {
    const formattedFileType = /[^\\]*\.(\w+)$/.exec(doc.orgnFlNm)[1];
    const formattedFileName = /(.*)\.[^.]+$/.exec(doc.orgnFlNm)[1];

    let docStatusLabel = '';
    switch (doc.docStatus) {
      case 'SFT_BATCH_INITIAL':
      case 'SFT_BATCH_INITIAL_QUEUED':
      case 'SFT_BATCH_SCHEDULER_RETRY':
      case 'SFT_BATCH_RETRY_QUEUED':
        docStatusLabel = 'processing';
        break;
      case 'SFT_BATCH_FAILED':
      case 'SNOW_CONV_FAIL':
        docStatusLabel = 'failed';
        break;
      case 'SFT_BATCH_COMPLETED':
      default:
        docStatusLabel = 'completed';
    }

    const date = new Date(doc.creatTs);
    let formattedTime = Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: 'numeric',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hourCycle: 'h12',
    });

    if (locale === 'en-AU') {
      const map = {};
      formattedTime.formatToParts(date).forEach(({ type, value }) => {
        map[type] = value;
      });
      formattedTime = `${map.day} ${map.month} ${map.year} at ${map.hour}:${map.minute} ${map.dayPeriod.toUpperCase()}`;
    } else {
      formattedTime = formattedTime.format(date).replace(/,/g, '');
    }

    const isExpanded = expanded[idx] || false;
    const toggleExpand = () => setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));

    const caseNumber = doc.indexResp.CASE_NUMBERS;
    const hasCaseNumber = caseNumber !== null && caseNumber !== undefined && caseNumber !== 'XXXX';
    const detailLine1 = `${hasCaseNumber ? `${caseNumber} | ` : ''}${formattedTime}  | ${formatBytes(doc.fileSize)} | ${formattedFileType.toUpperCase()}`;
    const docType = doc.indexResp.docTypeLabel;
    const acctType = doc.indexResp.accountTypeLabel;
    const detailLine2Parts = [docType, acctType].filter(Boolean);
    const detailLine2 = detailLine2Parts.join(' | ');
    /* istanbul ignore next */
    const badgeClass = badgeClassMap[docStatusLabel] || styles.badgeProcessing;

    return (
      // eslint-disable-next-line react/no-array-index-key -- index is stable for this static list
      <div key={`fileKey_${idx}`} className={styles.card} data-testid={`doc-card-${idx}`}>
        <button
          type="button"
          onClick={toggleExpand}
          aria-expanded={isExpanded}
          className={styles.cardHeader}
        >
          <div className={styles.titleArea}>
            <IconAttach title="attachment" titleId={`attach-icon-${idx}`} className={styles.attachIcon} />
            <span className={styles.docTitle}>{formatFileName(formattedFileName, 60)}</span>
          </div>
          <div className={styles.statusArea}>
            <span className={`${styles.badge} ${badgeClass}`}>
              <FormattedMessage id={`${docStatusLabel}Header`} />
            </span>
            <span
              className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}
              aria-hidden="true"
            >
              <IconChevronDown />
            </span>
          </div>
        </button>
        {isExpanded && (
          <div className={styles.details}>
            <div>{detailLine1}</div>
            {detailLine2 && <div>{detailLine2}</div>}
          </div>
        )}
      </div>
    );
  });

  return <div>{docMap}</div>;
};

DocList.defaultProps = {
  retrievedDocs: null,
  filePreview: null,
  downloadProgress: null,
};

DocList.propTypes = {
  retrievedDocs: PropTypes.instanceOf(Array),
  filePreview: PropTypes.func,
  downloadProgress: PropTypes.instanceOf(Object),
  locale: PropTypes.string.isRequired,
};

export default DocList;
